import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const CheckoutModal = ({ isOpen, onClose, selectedPlan = 'starter' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);

  // Fetch plans from database on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data, error } = await supabase?.from('plans')?.select('*')?.eq('is_active', true)?.order('price_cents', { ascending: true });

        if (error) throw error;
        setPlans(data || []);
      } catch (error) {
        console.error('Error fetching plans:', error);
        // Fallback to hardcoded plans if database fetch fails
        setPlans([
          {
            code: 'starter',
            name: 'Starter',
            price_cents: 7900,
            features: ['Up to 5 job postings', 'Basic scheduling', 'Email support']
          },
          {
            code: 'growth',
            name: 'Growth', 
            price_cents: 24900,
            features: ['Up to 25 job postings', 'Advanced AI scheduling', 'Real-time analytics', 'Priority support', 'Team collaboration']
          }
        ]);
      }
    };

    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen]);

  // Find current plan from database or fallback
  const currentPlan = plans?.find(plan => plan?.code === selectedPlan) || plans?.[0] || {
    code: 'starter',
    name: 'Starter',
    price_cents: 7900,
    features: ['Up to 5 job postings', 'Basic scheduling', 'Email support']
  };

  useEffect(() => {
    // Reset state when modal opens/closes
    if (isOpen) {
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  const handleCheckout = async () => {
    setLoading(true);
    setError('');

    try {
      // Validate that we have a valid plan code
      if (!currentPlan?.code || !['starter', 'growth', 'scale']?.includes(currentPlan?.code)) {
        throw new Error('Invalid plan selected. Please choose a valid subscription plan.');
      }

      // Validate Stripe configuration
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Payment system is temporarily unavailable. Please try again in a few moments.');
      }

      // Check if Stripe publishable key is properly configured
      if (!import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY || 
          import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY === 'your-stripe-publishable-key-here') {
        throw new Error('Payment system is not properly configured. Please contact support.');
      }

      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('Please sign in to continue with your subscription.');
      }

      // For now, simulate successful checkout and redirect to onboarding
      // In production, this would create a Stripe checkout session via Supabase Edge Function
      console.log('Checkout initiated for:', {
        planCode: currentPlan?.code,
        planName: currentPlan?.name,
        priceInCents: currentPlan?.price_cents,
        userId: user?.id
      });

      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Redirect to onboarding page
      window.location.href = `/onboarding?plan=${currentPlan?.code}&status=checkout_success`;
      
    } catch (error) {
      console.error('Checkout error:', error);
      
      // Handle specific error types with user-friendly messages
      if (error?.message?.includes('sign in')) {
        setError('Please sign in to your account before subscribing to a plan.');
      } else if (error?.message?.includes('Invalid plan')) {
        setError('The selected plan is no longer available. Please choose a different plan.');
      } else if (error?.message?.includes('Payment system')) {
        setError(error?.message);
      } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
        setError('Connection error. Please check your internet connection and try again.');
      } else {
        setError('Unable to process subscription. Please try again or contact support if the problem persists.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-[#1C1C1E]">Start Your Free Trial</h2>
              <p className="text-sm text-gray-600 mt-1">
                14-day free trial, then ${(currentPlan?.price_cents / 100)?.toFixed(2)}/month
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Plan Details */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#5E6AD2] to-[#AEB8FE] rounded-2xl mb-4">
              <Icon name="Zap" size={24} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-[#1C1C1E] mb-2">
              {currentPlan?.name} Plan
            </h3>
            <div className="text-3xl font-bold text-[#1C1C1E] mb-1">
              ${(currentPlan?.price_cents / 100)?.toFixed(2)}
              <span className="text-lg text-gray-600 font-normal">/month</span>
            </div>
            <p className="text-sm text-gray-600">
              after 14-day free trial
            </p>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h4 className="font-semibold text-[#1C1C1E] mb-3">What's included:</h4>
            <ul className="space-y-2">
              {currentPlan?.features?.map((feature, index) => (
                <li key={index} className="flex items-center text-sm">
                  <Icon name="Check" size={16} className="text-[#5E6AD2] mr-3 flex-shrink-0" />
                  <span className="text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Trial Benefits */}
          <div className="bg-[#F2F4FF] rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-[#1C1C1E] mb-2 flex items-center">
              <Icon name="Gift" size={16} className="text-[#5E6AD2] mr-2" />
              Free Trial Benefits
            </h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• Full access to all features</li>
              <li>• No credit card required to start</li>
              <li>• Cancel anytime during trial</li>
              <li>• Easy upgrade or downgrade</li>
            </ul>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={16} className="text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-800 text-sm font-medium">Subscription Error</p>
                  <p className="text-red-700 text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full bg-[#5E6AD2] hover:bg-[#5E6AD2]/90 text-white py-3 text-base font-medium"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="CreditCard" size={16} className="mr-2" />
                  Start Free Trial
                </>
              )}
            </Button>

            <Button
              variant="ghost"
              onClick={onClose}
              className="w-full text-gray-600 hover:text-gray-800"
            >
              Maybe later
            </Button>
          </div>

          {/* Security Notice */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
              <Icon name="Shield" size={14} />
              <span>Secured by Stripe • SSL encrypted • No spam, ever</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;